import { Injectable, signal, effect, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../../environments/environment';
;
export type SocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error';
interface SocketMessage<T = any> {
  event: string;
  data?: T;
}

@Injectable({
  providedIn: 'root'
})

export class SocketService {
  private authService = inject(AuthService);
  private socket: WebSocket | null = null;
  private readonly socketUrl = `${environment.socketServerUrl}`; // Your backend URL

  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private readonly reconnectDelay = 1000;
  
  // Status signal
  status = signal<SocketStatus>('disconnected');
  
  // Connection ID for debugging
  socketId = signal<string | null>(null);
  
  constructor() {
    // Auto-connect when user is authenticated
    effect(() => {
      const user = this.authService.user();
      if (user && !this.socket) {
        this.connect();
      } else if (!user && this.socket) {
        this.disconnect();
      }
    });
  }
  
  connect(): void {

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    
    this.status.set('connecting');

    
    const user = this.authService.user();

    if (!user) {
      console.warn('Cannot connect: user not authenticated');
      return;
    }

    this.status.set('connecting');

    const url = new URL(this.socketUrl);
    url.searchParams.set('token',user.token.toString());
    url.searchParams.set('userId',user.id);

    this.socket = new WebSocket(url.toString());
  
    // Connect with authentication
    this.socket.onopen = () => {
      this.status.set('connected');
      this.reconnectAttempts = 0;
      console.log('Websocket connected')
      console.log('this is waht were sending ', user)

      this.emit('registerUser', {
        type:'user',
        ...user
      })
    }

       
    // Connection events
   this.socket.onclose = () => {
      this.status.set('disconnected');
      this.socketId.set(null);
      console.log('WebSocket disconnected');

      this.tryReconnect();
    };
    
    this.socket.onerror = (error) => {
      this.status.set('error');
      console.error('WebSocket error:', error);
    };
    
    this.socket.onmessage = (event) => {
      this.handleMessage(event.data);
    };
    
  }
  
  private tryReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;

    this.reconnectAttempts++;

    setTimeout(() => {
      console.log(`Reconnecting... (${this.reconnectAttempts})`);
      this.connect();
    }, this.reconnectDelay);
  }

    disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.status.set('disconnected');
    }
  }

  emit(event: string, data?: any): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn(`Socket not connected. Event "${event}" not sent.`);
      return;
    }

    const message: SocketMessage = { event, data };
    this.socket.send(JSON.stringify(message));
  }

  private messageHandlers = new Map<string, Set<(data: any) => void>>();

  private handleMessage(raw: string): void {
    try {
      const message: SocketMessage = JSON.parse(raw);
      const handlers = this.messageHandlers.get(message.event);

      handlers?.forEach(handler => handler(message.data));
    } catch (e) {
      console.error('Invalid WebSocket message:', raw);
    }
  }
  
  
  // Listen to events
  on<T = any>(event: string): Observable<T> {
    return new Observable<T>(observer => {
      const handler = (data: T) => observer.next(data);

      if (!this.messageHandlers.has(event)) {
        this.messageHandlers.set(event, new Set());
      }

      this.messageHandlers.get(event)!.add(handler);

      return () => {
        this.messageHandlers.get(event)?.delete(handler);
      };
    });
  }
  
  // Join a room
  joinRoom(room: string): void {
    this.emit('join', room);
  }
  
  // Leave a room
  leaveRoom(room: string): void {
    this.emit('leave', room);
  }
  
  // Get socket instance (for advanced usage)
  getSocket(): WebSocket | null {
    return this.socket;
  }
}